import { FormProvider, useForm } from 'react-hook-form';
import { addressDtoSchema, initialAddressDto } from './form-config';
import { useEffect, useMemo, useState } from 'react';

import ChevronDownAdornment from '@atoms/ChevronDownAdornment';
import { DevTool } from '@hookform/devtools';
import { EstablishmentAddressFormDto } from '@establishment/models/establishment-form';
import { EstablishmentBlockStyled } from '../establishment-block.styled';
import EstablishmentFormFooter from '../EstablishmentFormFooter';
import IOption from '@models/option';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import JTextFieldBase from '@atoms/JTextFieldBase';
import JTextFieldForm from '@atoms/JTextFieldForm';
import Jpaper from '@atoms/JPaper';
import { Stack } from '@mui/material';
import { useCities } from '@/queries/useCities';
import { useCountries } from '@/queries/useCountries';
import { useDebouncedCallback } from 'use-debounce';
import useEstablishmentMutation from '@establishment/hooks/useEstablishmentMutation';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

interface EstablishmentFormDetailsProps {
  includeFooter?: boolean;
}
const EstablishmentFormAddress = ({
  includeFooter,
}: EstablishmentFormDetailsProps) => {
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [countryInputValue, setCountryInputValue] = useState<string>('');
  const { setStepIndex, payload, setPayload, setIsDirty } =
    useEstablishmentMutation();
  const { data: cities } = useCities(10, 1, cityInputValue);
  const { data: countries } = useCountries(10, 1, countryInputValue);
  const { t } = useTranslation();
  const methods = useForm<EstablishmentAddressFormDto>({
    mode: 'all',
    defaultValues: useMemo(() => initialAddressDto, [initialAddressDto]),
    resolver: yupResolver(addressDtoSchema(t)),
  });
  const {
    handleSubmit,
    control,
    formState,
    setValue,
    trigger,
    getValues,
    watch,
  } = methods;
  const { isValid, isDirty } = formState;

  const onSubmit = handleSubmit((data) => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });
  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);

  const debouncedCountry = useDebouncedCallback((value) => {
    setCountryInputValue(value);
  }, 1000);
  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];

  const countriesOptions = countries
    ? countries.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  const setCityAndZipCode = (option: IOption): void => {
    if (!option) {
      setValue('cityId', '');
      setValue('cityName', '');
    } else {
      setValue('cityId', option.value, { shouldDirty: true });
      setValue('cityName', option.label, { shouldDirty: true });
    }

    trigger(['cityId', 'cityName']);
  };

  const setCountry = (option: IOption): void => {
    if (!option) {
      setValue('countryId', '');
      setValue('countryName', '');
    } else {
      setValue('countryId', option.value, { shouldDirty: true });
      setValue('countryName', option.label, { shouldDirty: true });
      setValue('cityId', '');
      setValue('cityName', '');
    }

    trigger(['countryId', 'countryName', 'cityId', 'cityName']);
  };

  const removeCountryfromPayload = (): void => {
    if (!payload?.addressDto?.countryName) return;
    if (payload?.addressDto) {
      setPayload((prev) => {
        if (!prev?.addressDto) return;
        return {
          ...prev,
          addressDto: {
            ...prev.addressDto,
            countryName: '',
            countryId: '',
          },
        };
      });
    }
  };
  const removeCityfromPayload = (): void => {
    if (!payload?.addressDto?.cityName) return;
    if (payload?.addressDto) {
      setPayload((prev) => {
        if (!prev?.addressDto) return;
        return {
          ...prev,
          addressDto: {
            ...prev.addressDto,
            cityName: '',
            cityId: '',
          },
        };
      });
    }
  };
  useEffect(() => {
    if (isDirty) return;

    if (payload?.addressDto) {
      setValue('street', payload?.addressDto?.street);
      setValue('cityName', payload?.addressDto.cityName);
      setValue('countryId', payload?.addressDto.countryId);
      setValue('countryName', payload?.addressDto.countryName);
      setValue('additionalStreet', payload?.addressDto.additionalStreet);
      trigger();
    }
  }, [setValue, payload]);

  useEffect(() => {
    if (!payload?.addressDto?.cityName || isDirty) return;
    setValue('cityName', payload?.addressDto?.cityName);
  }, [payload, isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const values = getValues();
    setPayload((prev) => {
      if (!prev) return prev;
      return {
        establishmentDto: prev?.establishmentDto,
        addressDto: values,
        thumbnail: prev.thumbnail,
        video: prev.video,
        thumbnailUrl: prev.thumbnailUrl,
        videoUrl: prev.videoUrl,
        id: prev?.id,
      };
    });
  }, [
    setPayload,
    isDirty,
    watch('street'),
    watch('additionalStreet'),
    watch('countryId'),
    watch('cityName'),
  ]);

  useEffect(() => {
    setIsDirty(isDirty && isValid);
  }, [isDirty, isValid]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off">
        <Stack gap={2}>
          <EstablishmentBlockStyled>
            <Jpaper gap={1} alignItems="stretch" attr={{ fullHeight: true }}>
              <JTextFieldForm
                required
                name="street"
                fullWidth
                noBorder
                label={t('ADRESS_1')}
                InputLabelProps={{
                  shrink: !!payload?.addressDto?.street,
                }}
              />
              <JTextFieldForm
                name="additionalStreet"
                fullWidth
                noBorder
                label={t('ADRESS_2')}
                InputLabelProps={{
                  shrink: !!payload?.addressDto?.additionalStreet,
                }}
              />

              <JAutocomplete
                value={
                  payload?.addressDto?.countryId
                    ? {
                        label: payload?.addressDto?.countryName,
                        value: payload?.addressDto?.countryId,
                      }
                    : null
                }
                fullWidth
                label={t('COUNTRY')}
                options={countriesOptions}
                renderInput={(params) => (
                  <JTextFieldBase
                    onChange={(ev) => {
                      debouncedCountry(ev.target.value);
                    }}
                    required
                    label={t('COUNTRY')}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <ChevronDownAdornment />,
                    }}
                  />
                )}
                onChange={(ev, newValue) => {
                  removeCountryfromPayload();
                  setCountry(newValue as IOption);
                }}
                onTextChange={(val) => {
                  removeCountryfromPayload();
                  debouncedCountry(val);
                }}
              />

              {getValues('countryName')?.toUpperCase() === 'FRANCE' ? (
                <JAutocomplete
                  value={
                    payload?.addressDto?.cityName
                      ? {
                          label: payload?.addressDto?.cityName ?? '',
                          value: payload?.addressDto?.cityId ?? '',
                        }
                      : null
                  }
                  fullWidth
                  label={t('CITY_ZIP_CODE')}
                  options={citiesOptions}
                  renderInput={(params) => (
                    <JTextFieldBase
                      onChange={(ev) => {
                        debouncedCity(ev.target.value);
                      }}
                      required
                      label={t('CITY_ZIP_CODE')}
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <ChevronDownAdornment />,
                      }}
                    />
                  )}
                  onChange={(ev, newValue) => {
                    removeCityfromPayload();
                    setCityAndZipCode(newValue as IOption);
                  }}
                  onTextChange={(val) => {
                    removeCityfromPayload();
                    debouncedCity(val);
                  }}
                />
              ) : (
                <JTextFieldForm
                  data-qa="cityName"
                  name="cityName"
                  fullWidth
                  noBorder
                  label={t('CITY')}
                  InputLabelProps={{
                    shrink: !!payload?.addressDto?.street,
                  }}
                />
              )}
            </Jpaper>
          </EstablishmentBlockStyled>
          {includeFooter && (
            <EstablishmentFormFooter
              submitTab={false}
              isValid={isValid}
              values={getValues()}
            />
          )}
        </Stack>
      </form>
      {process.env.NODE_ENV !== 'production' && <DevTool control={control} />}
    </FormProvider>
  );
};

export default EstablishmentFormAddress;
